import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Tag,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  AlertCircle,
  Check,
  PackagePlus,
  ChevronDown,
  ArrowLeft,
  CalendarIcon,
} from "lucide-react";

function createEmptyForm() {
  return {
    code: "",
    discountPercent: "",
    scope: "global",
    active: true,
    expiresAt: "", // yyyy-MM-dd, optional
    usageLimit: "", // optional
  };
}

function couponToForm(c) {
  return {
    code: c.code ?? "",
    discountPercent: c.discountPercent != null ? String(c.discountPercent) : "",
    scope: c.scope ?? "global",
    active: c.active !== false,
    expiresAt: c.expiresAt ?? "",
    usageLimit: c.usageLimit != null ? String(c.usageLimit) : "",
  };
}

function validateCouponForm(form) {
  const errors = {};
  const code = form.code.trim();
  if (!code) errors.code = "Coupon code is required.";
  else if (!/^[A-Za-z0-9_-]{3,20}$/.test(code))
    errors.code = "Use 3–20 letters/numbers/hyphens, no spaces.";

  const pct = Number(form.discountPercent);
  if (form.discountPercent === "")
    errors.discountPercent = "Discount % is required.";
  else if (Number.isNaN(pct) || pct <= 0 || pct > 100)
    errors.discountPercent = "Enter a percent between 1 and 100.";

  if (form.usageLimit !== "") {
    const limit = Number(form.usageLimit);
    if (!Number.isInteger(limit) || limit <= 0)
      errors.usageLimit = "Usage limit must be a whole number greater than 0.";
  }
  return errors;
}

/**
 * @param {{
 *   createCoupon: () => Promise<Record<string, any>>; // { [templateId]: { subject, body, instruction, images } }
 *   updateCoupon: (id: string, payload: any) => Promise<any>;
 *   deleteCoupon: (id: string) => Promise<any>;
 *   getAllCoupons: () => Promise<Record<string, any>>; // { [templateId]: { subject, body, instruction, images } }
 *   COUPON_SCOPES: string[];
 * }} props
 */
export default function CreateCouponAdmin({
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getAllCoupons,
  COUPON_SCOPES,
}) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState("manage"); // "form" | "manage"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const AnyPopoverContent = /** @type {any} */ (PopoverContent);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await getAllCoupons();
      setCoupons(list);
    } catch (err) {
      console.error("Failed to load coupons:", err);
      setLoadError(err?.message || "Failed to load coupons.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "manage") fetchCoupons();
  }, [view, fetchCoupons]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));
  const fieldError = (field) => (touched[field] && errors[field]) || null;

  const resetForm = () => {
    setForm(createEmptyForm());
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const startCreate = () => {
    setEditingId(null);
    resetForm();
    setView("form");
  };

  const startEdit = (coupon) => {
    setEditingId(coupon.id);
    setForm(couponToForm(coupon));
    setErrors({});
    setTouched({});
    setSubmitError(null);
    setSubmitSuccess(false);
    setView("form");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    const validationErrors = validateCouponForm(form);
    setErrors(validationErrors);
    setTouched(
      Object.keys(form).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    );
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      code: form.code.trim(),
      discountPercent: Number(form.discountPercent),
      scope: form.scope,
      active: form.active,
      expiresAt: form.expiresAt || null,
      usageLimit: form.usageLimit === "" ? null : Number(form.usageLimit),
    };

    setIsSubmitting(true);
    try {
      if (editingId) {
        await updateCoupon(editingId, payload);
      } else {
        await createCoupon(payload);
      }
      setSubmitSuccess(true);
      if (!editingId) resetForm();
    } catch (err) {
      console.error("Failed to save coupon:", err);
      setSubmitError(err?.message || "Failed to save coupon.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteCoupon(deleteTarget.id);
      setCoupons((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete coupon:", err);
      setLoadError(err?.message || "Failed to delete coupon.");
    } finally {
      setIsDeleting(false);
    }
  };

  const inputClass = (hasError) =>
    `w-full h-12 rounded-xl border px-4 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0B2421]/30 ${
      hasError ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"
    }`;

  return (
    <div className="min-h-screen bg-[#F7F8F7] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between w-full mb-2">
          <div className="flex items-center gap-3">
            {view === "form" && editingId && (
              <button
                type="button"
                onClick={() => setView("manage")}
                className="h-9 w-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <h1 className="text-3xl font-bold text-[#0B2421]">
              {view === "manage"
                ? "Manage Coupons"
                : editingId
                  ? "Edit Coupon"
                  : "New Coupon"}
            </h1>
          </div>
          {view === "form" ? (
            <button
              type="button"
              onClick={() => setView("manage")}
              className="h-12 px-6 rounded-xl border border-[#0B2421]/20 text-[#0B2421] font-semibold hover:bg-[#0B2421]/5 transition-colors"
            >
              Manage Coupons
            </button>
          ) : (
            <button
              type="button"
              onClick={startCreate}
              className="h-12 px-6 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold flex items-center gap-2"
            >
              <PackagePlus className="h-4 w-4" />
              New Coupon
            </button>
          )}
        </div>
        <p className="text-gray-500 mt-1 mb-6">
          {view === "manage"
            ? "Edit or remove existing coupons."
            : "A coupon's scope decides which part of the total it discounts."}
        </p>

        {view === "manage" ? (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading coupons...
              </div>
            ) : loadError ? (
              <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{loadError}</span>
              </div>
            ) : coupons.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center">
                <Tag className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No coupons yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {coupons.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-gray-100 bg-white shadow-sm p-4"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-[#0B2421]/5 flex items-center justify-center flex-shrink-0">
                        <Tag className="h-4 w-4 text-[#0B2421]" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-[#0B2421] truncate">
                          {c.code}
                        </p>
                        <p className="text-xs text-gray-500">
                          {c.discountPercent}% off ·{" "}
                          {COUPON_SCOPES.find((s) => s.value === c.scope)
                            ?.label || c.scope}
                          {c.active === false ? " · disabled" : ""}
                          {c.usageLimit != null
                            ? ` · ${c.timesUsed || 0}/${c.usageLimit} used`
                            : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="h-9 px-3 rounded-lg border border-gray-200 text-sm font-semibold text-[#0B2421] hover:bg-gray-50 flex items-center gap-1.5"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(c)}
                        className="h-9 px-3 rounded-lg border border-red-200 text-sm font-semibold text-red-600 hover:bg-red-50 flex items-center gap-1.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <AnimatePresence>
              {deleteTarget && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                  onClick={() => setDeleteTarget(null)}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white rounded-2xl p-6 max-w-sm w-full"
                  >
                    <h3 className="text-lg font-bold text-[#0B2421] mb-2">
                      Delete coupon?
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                      This will permanently remove "{deleteTarget.code}". This
                      can't be undone.
                    </p>
                    <div className="flex items-center gap-3 justify-end">
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(null)}
                        disabled={isDeleting}
                        className="h-10 px-4 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 disabled:opacity-40"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={confirmDelete}
                        disabled={isDeleting}
                        className="h-10 px-4 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                      >
                        {isDeleting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          "Delete"
                        )}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
            {submitSuccess && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                <Check className="h-4 w-4" />
                {editingId ? "Coupon updated." : "Coupon created."}
              </div>
            )}
            {submitError && (
              <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                  Coupon Code <span className="text-[#D4AF37]">*</span>
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setField("code", e.target.value.toUpperCase())
                  }
                  onBlur={() => markTouched("code")}
                  placeholder="e.g., GOLF20"
                  className={inputClass(fieldError("code"))}
                />
                {fieldError("code") && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {fieldError("code")}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                    Discount % <span className="text-[#D4AF37]">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.discountPercent}
                    onChange={(e) =>
                      setField("discountPercent", e.target.value)
                    }
                    onBlur={() => markTouched("discountPercent")}
                    placeholder="e.g., 15"
                    className={inputClass(fieldError("discountPercent"))}
                  />
                  {fieldError("discountPercent") && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {fieldError("discountPercent")}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                    Applies To <span className="text-[#D4AF37]">*</span>
                  </label>
                  <select
                    value={form.scope}
                    onChange={(e) => setField("scope", e.target.value)}
                    className={inputClass(false)}
                  >
                    {COUPON_SCOPES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                    Expiry Date (optional)
                  </label>

                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm transition-colors hover:border-[#0B2421]"
                      >
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="h-4 w-4 text-[#D4AF37] shrink-0" />

                          <span
                            className={
                              form.expiresAt
                                ? "text-[#0B2421] font-medium"
                                : "text-gray-400"
                            }
                          >
                            {form.expiresAt
                              ? format(
                                  new Date(`${form.expiresAt}T00:00:00`),
                                  "dd MMM yyyy",
                                )
                              : "Select expiry date"}
                          </span>
                        </div>

                        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                      </button>
                    </PopoverTrigger>

                    <AnyPopoverContent
                      className="w-auto p-0"
                      align="start"
                      sideOffset={8}
                    >
                      <Calendar
                        mode="single"
                        selected={
                          form.expiresAt
                            ? new Date(`${form.expiresAt}T00:00:00`)
                            : undefined
                        }
                        onSelect={(date) => {
                          if (date) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(
                              2,
                              "0",
                            );
                            const day = String(date.getDate()).padStart(2, "0");

                            setField("expiresAt", `${year}-${month}-${day}`);
                          } else {
                            setField("expiresAt", "");
                          }

                          setDateOpen(false);
                        }}
                      />
                    </AnyPopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                    Usage Limit (optional)
                  </label>

                  <input
                    type="number"
                    min="1"
                    value={form.usageLimit}
                    onChange={(e) => setField("usageLimit", e.target.value)}
                    onBlur={() => markTouched("usageLimit")}
                    placeholder="e.g., 100"
                    className={inputClass(fieldError("usageLimit"))}
                  />

                  {fieldError("usageLimit") && (
                    <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {fieldError("usageLimit")}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-[#0B2421] mb-2 block">
                  Status
                </label>
                <div className="flex rounded-xl border border-gray-200 bg-gray-50 p-1 w-fit">
                  {[
                    { label: "Active", value: true },
                    { label: "Disabled", value: false },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setField("active", opt.value)}
                      className={`h-9 px-5 rounded-lg text-sm font-semibold transition-colors ${
                        form.active === opt.value
                          ? "bg-[#0B2421] text-white"
                          : "text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 px-8 rounded-xl bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold disabled:opacity-40 flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {editingId ? "Saving..." : "Creating..."}
                    </>
                  ) : editingId ? (
                    "Save Changes"
                  ) : (
                    "Create Coupon"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
